/**
 * Multi-deal registry — Eagle + Juno + Sedgwick (extensible). Metadata only; no CA numbers.
 * Draft stubs (admin VDR onboard) overlay via setDraftDeals — never baked into git.
 */
import dealsDoc from './deals.js';

export const STORAGE_KEY = 'd2d.energy.selectedDealId';
export const DEAL_QUERY_KEY = 'deal';

export const DEALS = Object.freeze(dealsDoc.deals.map((d) => Object.freeze({ ...d })));
export const DEFAULT_DEAL_ID = dealsDoc.defaultDealId || DEALS[0]?.dealId || 'eagle-etx-2026';

const BY_ID = Object.fromEntries(DEALS.map((d) => [d.dealId, d]));

/** Runtime draft stubs (from admin API / localStorage). Not frozen into DEALS. */
let draftDeals = [];
let draftById = Object.create(null);

export function setDraftDeals(drafts) {
  const list = Array.isArray(drafts) ? drafts : [];
  // Never shadow a published registry dealId.
  draftDeals = list
    .filter((d) => d && d.dealId && !BY_ID[d.dealId])
    .map((d) => Object.freeze({ ...d, draft: true }));
  draftById = Object.fromEntries(draftDeals.map((d) => [d.dealId, d]));
  return draftDeals;
}

export function listDraftDeals() {
  return draftDeals;
}

export function listDeals({ includeDrafts = true } = {}) {
  if (!includeDrafts || !draftDeals.length) return DEALS;
  return [...DEALS, ...draftDeals];
}

export function getDeal(dealId) {
  if (!dealId) return null;
  return BY_ID[dealId] || draftById[dealId] || null;
}

export function resolveDealId(candidate) {
  if (candidate && (BY_ID[candidate] || draftById[candidate])) return candidate;
  return DEFAULT_DEAL_ID;
}

export function getDefaultDeal() {
  return getDeal(DEFAULT_DEAL_ID) || DEALS[0];
}

/** Read persisted selection (browser only). */
export function readStoredDealId() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && getDeal(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeStoredDealId(dealId) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const id = resolveDealId(dealId);
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearStoredDealId() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Base URL for a deal's dashboard JSON (no trailing slash).
 * Eagle keeps flat `data/`; later deals nest under `data/{dealId}/`.
 */
export function dealDataBaseUrl(dealOrId, baseUrl = '/') {
  const deal = typeof dealOrId === 'string' ? getDeal(dealOrId) : dealOrId;
  const root = String(baseUrl || '/').replace(/\/?$/, '/');
  if (!deal || deal.legacyFlat || !deal.dataPrefix) {
    return `${root}data`;
  }
  return `${root}data/${deal.dataPrefix}`;
}

/** Absolute-ish fetch path for a named JSON file under the deal's data root. */
export function dealDataUrl(dealOrId, fileName, baseUrl = '/') {
  const base = dealDataBaseUrl(dealOrId, baseUrl);
  const name = String(fileName || '').replace(/^\//, '');
  return `${base}/${name}`;
}

/**
 * Normalize a hash path segment: strip leading `#` / `/` so `#/projects` and
 * `#projects` both resolve to `projects`.
 */
export function normalizeHashPath(rawPath = '') {
  return String(rawPath || '')
    .replace(/^#/, '')
    .replace(/^\/+/, '');
}

/**
 * Parse `#projects` / `#/projects` or `#decision?deal=juno-wtx-2026` deal hints.
 * Returns { viewProjects, dealIdFromHash } — dealIdFromHash may be null.
 */
export function parseDealFromHash(rawHash = '') {
  const full = String(rawHash || '').replace(/^#/, '');
  const qIdx = full.indexOf('?');
  const pathRaw = qIdx >= 0 ? full.slice(0, qIdx) : full;
  // Leading slash (`#/projects`) must not yield path === '' via split('/')[0].
  const path = normalizeHashPath(pathRaw).split('/')[0];
  const query = qIdx >= 0 ? full.slice(qIdx + 1) : '';
  const params = new URLSearchParams(query);
  const fromQ = params.get(DEAL_QUERY_KEY);
  return {
    viewProjects: path === 'projects' || path === 'deals',
    dealIdFromHash: fromQ && getDeal(fromQ) ? fromQ : null,
  };
}

/** Ensure `deal=<id>` is present on a hash path+query string (no leading #). */
export function withDealQuery(hashPathAndQuery, dealId) {
  const raw = String(hashPathAndQuery || '').replace(/^#/, '');
  const id = resolveDealId(dealId);
  const qIdx = raw.indexOf('?');
  const path = qIdx >= 0 ? raw.slice(0, qIdx) : raw;
  const query = qIdx >= 0 ? raw.slice(qIdx + 1) : '';
  const params = new URLSearchParams(query);
  params.set(DEAL_QUERY_KEY, id);
  const q = params.toString();
  return q ? `${path}?${q}` : path;
}
